import { LitElement, html, nothing, type TemplateResult } from 'lit';
import { property, state, customElement } from 'lit/decorators.js';
import { UserService } from '@lib/user-service';
import '../shopping-cart-counter/shopping-cart-counter';
import '../account-badge/account-badge';

interface MenuItem {
    ID: number;
    title: string;
    url: string;
    classes?: string[];
    children?: MenuItem[];
    preview_image_url: string;
}

const HEADER_BREAKPOINT = 1100;

@customElement('custom-navigation')
export class CustomNavigation extends LitElement {
    protected createRenderRoot(): HTMLElement | DocumentFragment {
        return this;
    }

    private userService = UserService.getInstance();

    @state()
    private getNonceHeader: any;

    @property({ type: String }) logo = '';
    @property({ type: String, attribute: 'menu-id' }) menuId = '3';
    @property({ type: String, attribute: 'base-url' }) baseUrl =
        window.location.origin;
    @property({ type: Boolean, attribute: 'include-permalink' })
    includePermalink = false;

    @state() private menuItems: MenuItem[] = [];
    @state() private isOpen = false;
    @state() private loading = true;
    @state() private error = false;
    @state() private openSubmenus: Set<number> = new Set();

    async connectedCallback() {
        super.connectedCallback();
        const getNonceHeader = this.userService.getNonceHeader();
        this.getNonceHeader = getNonceHeader;
        this.loadMenu();
        window.addEventListener('resize', this.onResize);
        window.addEventListener('click', this.onGlobalClick);
    }

    disconnectedCallback() {
        window.removeEventListener('resize', this.onResize);
        super.disconnectedCallback();
    }

    private onGlobalClick = (event: MouseEvent) => {
        const openDropdowns = document.querySelectorAll('.dropdown-open');
        openDropdowns.forEach((dropdown) => {
            if (
                dropdown instanceof HTMLElement &&
                !dropdown.contains(event.target as Node) &&
                !dropdown.previousElementSibling?.contains(event.target as Node)
            ) {
                dropdown.classList.remove('dropdown-open');
            }
        });
    };

    private onResize = () => {
        if (this.isMobile()) {
            this.isOpen = false;
        }
        this.requestUpdate();
    };

    private get apiUrl(): string {
        const cleanBase = this.baseUrl.replace(/\/$/, '');
        let url = `${cleanBase}/wp-json/wp-headless/v1/menu/${this.menuId}`;
        if (this.includePermalink) {
            url += `?permalink=${encodeURIComponent(window.location.href)}`;
        }
        return url;
    }

    private async loadMenu() {
        try {
            let response;
            if (!this.userService.nonceHeaderExisting()) {
                response = await fetch(this.apiUrl);
            } else {
                response = await fetch(this.apiUrl, {
                    credentials: 'include',
                    headers: {
                        'X-WP-Nonce': this.getNonceHeader,
                    },
                });
            }
            if (!response.ok) throw new Error(`HTTP ${response.status}`);
            const data = await response.json();
            this.menuItems = data.items || data || [];
            this.logo = data.logo;
        } catch (e) {
            console.error('Menu error:', e);
            this.error = true;
        } finally {
            this.loading = false;
        }
    }

    private isMobile(): boolean {
        return window.innerWidth >= HEADER_BREAKPOINT;
    }

    private toggleMobileNav() {
        this.isOpen = !this.isOpen;
    }

    private closeMobileNav() {
        this.isOpen = false;
    }

    private toggleSubmenu(id: number): void {
        if (this.openSubmenus.has(id)) {
            this.openSubmenus.delete(id);
        } else {
            this.openSubmenus.add(id);
        }
        this.requestUpdate();
    }

    private hasChildren(item: MenuItem): boolean {
        const children = item.children;

        if (!children) return false;

        if (Array.isArray(children)) {
            return children.length > 0;
        }

        if (typeof children === 'object') {
            return Object.keys(children).length > 0;
        }

        return false;
    }

    private renderDesktop() {
        return html`
            <header class="custom-header">
                <a href="/" class="custom-header__logo-link">
                    ${this.logo
                        ? html`<img
                              src="${this.logo}"
                              alt="Logo"
                              class="custom-header__logo"
                          />`
                        : html`<div class="custom-header__logo-placeholder">
                              Logo
                          </div>`}
                </a>
                <nav class="custom-header__nav">
                    <ul class="custom-header__menu">
                        ${this.menuItems.map((item) =>
                            this.renderListItem(item)
                        )}

                        <ds-shopping-cart-counter></ds-shopping-cart-counter>
                        <km-account-badge></km-account-badge>
                    </ul>
                </nav>
            </header>
        `;
    }

    private renderListItem(item: MenuItem): TemplateResult {
        const itemHasChildren = this.hasChildren(item);

        return html`
            <li class="${(item.classes || []).join(' ')} flex items-center">
                ${this.renderMenuItem(itemHasChildren, item)}
                ${this.renderSubMenuDropdown(itemHasChildren, item)}
            </li>
        `;
    }

    private renderMenuItem(
        itemHasChildren: boolean,
        item: MenuItem
    ): TemplateResult {
        let template: TemplateResult;
        const isOpen = this.openSubmenus.has(item.ID);

        if (itemHasChildren) {
            template = html`
                <button
                    class="submenu-button"
                    @click="${() => this.triggerSubmenuDropdown(item.ID)}"
                >
                    ${item.title}
                    ${itemHasChildren
                        ? html`<i
                              class="ml-2 km-icon-angle-small-${isOpen
                                  ? 'up'
                                  : 'down'}"
                          ></i>`
                        : nothing}
                </button>
            `;
        } else {
            template = html`
                <a
                    href="${item.url}"
                    class="${this.isActive(item.url) ? 'active' : ''}"
                    >${item.title}
                    ${itemHasChildren
                        ? html`<i
                              class="ml-2 km-icon-angle-small-${isOpen
                                  ? 'up'
                                  : 'down'}"
                          ></i>`
                        : nothing}
                </a>
            `;
        }
        return template;
    }

    private triggerSubmenuDropdown(id: number): void {
        const cssClass = 'dropdown-open';
        const submenu = this.querySelector(`#${CSS.escape(String(id))}`);
        if (!submenu) return;

        const willOpen = !submenu.classList.contains(cssClass);

        this.querySelectorAll('.submenu-dropdown.' + cssClass).forEach((el) => {
            if (el !== submenu) el.classList.remove(cssClass);
        });

        submenu.classList.toggle(cssClass, willOpen);

        const trigger = submenu.previousElementSibling as HTMLElement | null;
        if (trigger?.hasAttribute('aria-expanded')) {
            trigger.setAttribute('aria-expanded', String(willOpen));
        }

        const isOpen = this.openSubmenus.has(id);
        this.openSubmenus.clear();

        if (!isOpen) {
            this.openSubmenus.add(id);
        }

        this.requestUpdate();
    }

    private renderSubMenuDropdown(itemHasChildren: boolean, item: MenuItem) {
        if (!itemHasChildren) return nothing;

        const rawChildren = item.children ?? [];
        const childrenArray: MenuItem[] = Array.isArray(rawChildren)
            ? rawChildren
            : Object.values(rawChildren);

        return html`
            <div id="${item.ID}" class="submenu-dropdown appear-opacity-simple">
                <div class="flex items-center gap-8">
                    <a href="/sortiment" class="text-secondary">
                        <h2 class="text-3xl text-left">Unser Sortiment</h2>
                    </a>
                    <div class="flex items-center w-full">
                        <ul class="flex w-full justify-between gap-5">
                            ${childrenArray.length
                                ? childrenArray.map((child) =>
                                      this.renderSubMenuItem(child)
                                  )
                                : nothing}
                        </ul>
                    </div>
                </div>
            </div>
        `;
    }

    private renderSubMenuItem(childItem: MenuItem) {
        return html`
            <li class="submenu-item">
                <a
                    href="${childItem.url}"
                    class="flex flex-col gap-2 child-item-link"
                >
                    <img src="${childItem.preview_image_url}" alt="" />
                    ${childItem.title}
                </a>
            </li>
        `;
    }

    private renderMobile() {
        return html`
            <header class="custom-header mobile">
                ${this.renderMobileHeader()}
                ${this.isOpen
                    ? html`
                          <nav class="custom-header__nav">
                              ${this.renderMobileMenu(this.menuItems)}
                          </nav>
                      `
                    : nothing}
            </header>
        `;
    }

    private renderMobileHeader() {
        return html`
            <div class="custom-header__row">
                <a href="/" class="custom-header__logo-link">
                    ${this.logo
                        ? html`<img
                              src="${this.logo}"
                              alt="Logo"
                              class="custom-header__logo"
                          />`
                        : nothing}
                </a>
                ${this.burgerButton()}
            </div>
        `;
    }

    private renderMobileMenu(items: MenuItem[]) {
        return html`
            <ul class="custom-header__menu open">
                <ds-shopping-cart-counter></ds-shopping-cart-counter>
                ${items.map((item) => {
                    const hasChildren = this.hasChildren(item);
                    const isOpen = this.openSubmenus.has(item.ID);

                    return html`
                        <li class="${(item.classes || []).join(' ')}">
                            ${hasChildren
                                ? html`
                                      <button
                                          class="flex justify-center items-center mobile-menu-item w-full text-left submenu-button"
                                          @click=${() =>
                                              this.toggleSubmenu(item.ID)}
                                      >
                                          ${item.title}
                                          <i
                                              class="ml-2 km-icon-angle-small-${isOpen
                                                  ? 'up'
                                                  : 'down'}"
                                          ></i>
                                      </button>
                                  `
                                : html`
                                      <a
                                          href="${item.url}"
                                          class="${this.isActive(item.url)
                                              ? 'active'
                                              : ''}"
                                          class="flex justify-between items-center mobile-menu-item w-full"
                                          @click=${this.closeMobileNav}
                                      >
                                          ${item.title}
                                      </a>
                                  `}
                            ${hasChildren && isOpen
                                ? html`<ul
                                      class="mobile-submenu gap-4 mt-4 mb-4"
                                  >
                                      ${(() => {
                                          const rawChildren =
                                              item.children ?? [];
                                          const childrenArray: MenuItem[] =
                                              Array.isArray(rawChildren)
                                                  ? rawChildren
                                                  : Object.values(rawChildren);

                                          return childrenArray.map(
                                              (child) => html`
                                                  <li>
                                                      <a
                                                          href="${child.url}"
                                                          @click=${this
                                                              .closeMobileNav}
                                                          >${child.title}</a
                                                      >
                                                  </li>
                                              `
                                          );
                                      })()}
                                  </ul>`
                                : nothing}
                        </li>
                    `;
                })}
                <km-account-badge></km-account-badge>
            </ul>
        `;
    }

    private isActive(url: string) {
        return url === window.location.pathname;
    }

    private burgerButton() {
        return html`
            <div
                class="custom-header__burger ${this.isOpen ? 'open' : ''}"
                @click=${this.toggleMobileNav}
            >
                <span></span><span></span><span></span>
            </div>
        `;
    }

    render() {
        if (this.error || !this.menuItems.length || this.loading)
            return nothing;

        return this.isMobile() ? this.renderDesktop() : this.renderMobile();
    }
}
